import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import ActionButton from "../buttons/ActionButton";
import { GcpScriptDto } from "utils/saastypes";
import { DownloadRounded } from "@mui/icons-material";

export default function GcpAuthorizeAccountDialog({
  gcpScriptData,
  handleClose,
  verifyMethod,
  progressPending = false,
  provisionRequest,
  setProvisionRequest,
}: {
  gcpScriptData: GcpScriptDto;
  verifyMethod: () => void;
  handleClose: () => void;
  progressPending?: boolean;
  provisionRequest: any;
  setProvisionRequest: (req: any) => void;
}) {
  const [jsonKey, setJsonKey] = useState("");
  const [jsonError, setJsonError] = useState("");

  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setJsonKey(value);

    // Validate JSON
    try {
      if (value.trim()) {
        const parsedKey = JSON.parse(value);
        setJsonError("");

        // Update provision request with parsed key
        setProvisionRequest({
          ...provisionRequest,
          gcpServiceAccountKey: parsedKey,
        });
      }
    } catch (e) {
      setJsonError("Invalid JSON format. Please paste the complete JSON key.");
    }
  };

  const handleVerify = () => {
    if (!jsonKey.trim()) {
      setJsonError("Please paste the GCP service account JSON key");
      return;
    }

    try {
      const parsedKey = JSON.parse(jsonKey);
      setProvisionRequest({
        ...provisionRequest,
        gcpServiceAccountKey: parsedKey,
      });
      verifyMethod();
    } catch (e) {
      setJsonError("Please enter a valid JSON key");
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open onClose={handleClose}>
      <DialogTitle>Authorize GCP Account</DialogTitle>
      <DialogContent>
        {/* Step 1: Download and Run Script */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Step 1: Download and Run Setup Script
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Download the script and run it in your GCP Cloud Shell or local
            terminal with gcloud CLI configured. The script will create a
            service account, custom IAM role, and generate a JSON key.
          </Typography>
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginTop: 16,
            }}
          >
            <div className={"headerText"} style={{ fontSize: 16 }}>
              Script File:
            </div>
            <div
              className={"headerText"}
              style={{
                marginLeft: 20,
                fontSize: 16,
                display: "flex",
                alignContent: "center",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              Download Script
              <IconButton
                component={"a"}
                target={"noreferrer noopener"}
                href={gcpScriptData.s3Url}
              >
                <DownloadRounded />
              </IconButton>
            </div>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Run the script with: <code>bash setup-script.sh</code>
          </Alert>
        </Box>

        {/* Step 2: Paste JSON Key */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Step 2: Paste Service Account JSON Key
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            After running the script, it will output a JSON key. Copy the entire
            JSON output and paste it below.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={jsonKey}
            onChange={handleJsonChange}
            error={!!jsonError}
            helperText={
              jsonError || "Paste the complete JSON service account key here"
            }
            placeholder="Paste the complete JSON key output from the setup script here..."
            sx={{
              fontFamily: "monospace",
              fontSize: "0.875rem",
              "& textarea": {
                fontFamily: "monospace !important",
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={progressPending}
          style={{ margin: 8, fontSize: 14, textTransform: "none" }}
          sx={{
            display: "inline",
            mx: 0.5,
          }}
        >
          Cancel
        </Button>
        <ActionButton
          variant="contained"
          color="primary"
          progress={progressPending}
          // @ts-ignore
          component={"a"}
          target="_blank"
          // @ts-ignore
          rel={"noreferrer noopener"}
          href={gcpScriptData.s3Url}
          style={{
            margin: 8,
            fontSize: 15,
            textTransform: "none",
            padding: 10,
          }}
          sx={{
            display: "inline",
            mx: 0.5,
          }}
        >
          Download Script File
        </ActionButton>
        <ActionButton
          variant="contained"
          color="success"
          progress={progressPending}
          onClick={handleVerify}
          disabled={!jsonKey || !!jsonError || progressPending}
          style={{
            margin: 8,
            fontSize: 15,
            textTransform: "none",
            padding: 10,
          }}
          sx={{
            display: "inline",
            mx: 0.5,
          }}
        >
          {progressPending ? "Verifying..." : "Verify & Continue"}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}
